import { useReducer, useLayoutEffect, useRef, useEffect } from 'react';
import { AddressbookOptionService, GoogleOptionsService } from '#service/autocomplete-service/service';
import isLatLng from '#utils/is-lat-lng';
import GeocodeGoogleOptionsService from '#service/autocomplete-service/service/geocode-google-options-service';
import { requestStatuses as statuses } from '#helper';

const defaultState = {
  status: statuses.idle,
  options: [],
  error: null
};

const actionTypes = Object.freeze({
  fetchStarted: 'fetchStarted',
  fetchSucceeded: 'fetchSucceeded',
  fetchFailed: 'fetchFailed',
  clear: 'clear'
});

const reducer = (state, action) => {
  switch (action.type) {
    case actionTypes.fetchStarted:
      return { ...state, status: statuses.loading, error: null };
    case actionTypes.fetchSucceeded:
      return { ...state, status: statuses.succeeded, options: action.payload };
    case actionTypes.fetchFailed:
      return { ...state, status: statuses.failed, options: [], error: action.error };
    case actionTypes.clear:
      return defaultState;
    default:
      return state;
  }
};

/**
 * Combines search results from
 * AddressbookOptionService and GoogleOptionsService.
 */

const useAddressbookGoogleOptionsService = () => {
  const [state, dispatch] = useReducer(reducer, defaultState);
  const isMounted = useRef(true);
  const googleOptionsService = useRef(null);
  const addressbookOptionsService = useRef(new AddressbookOptionService(['address']));
  const geocodeGoogleOptionsService = useRef(new GeocodeGoogleOptionsService());
  const abortController = useRef(new AbortController());

  useLayoutEffect(() => {
    if (!googleOptionsService.current && window.google) {
      googleOptionsService.current = new GoogleOptionsService(
        new window.google.maps.places.AutocompleteService(),
        new window.google.maps.places.PlacesService(document.createElement('div'))
      );
    }

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => () => {
    abortController.current.abort();
  }, []);

  const dispatchFetchOptionsStarted = () => dispatch({ type: actionTypes.fetchStarted });
  const dispatchClearOptions = () => dispatch({ type: actionTypes.clear });

  const fetchOptions = async (value, debounceTime = 500) => {
    try {
      abortController.current.abort();
      dispatch({ type: actionTypes.fetchStarted });
      abortController.current = new AbortController();
      const { signal } = abortController.current;
      await new Promise(res => {
        setTimeout(() => {
          res();
        }, debounceTime);
      });

      let newOptions;

      if (isLatLng(value)) {
        newOptions = await geocodeGoogleOptionsService.current?.getOptions(value, { signal });
      } else {
        newOptions = await addressbookOptionsService.current?.getOptions(value, { signal });

        if (newOptions?.length < 5) {
          const googleOptions = await googleOptionsService.current?.getOptions(value, { signal });
          newOptions.push(...googleOptions?.slice(0, 5 - newOptions.length) ?? []);
        }
      }

      if (isMounted.current) {
        dispatch({
          type: actionTypes.fetchSucceeded,
          payload: newOptions ?? []
        });
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        return;
      }
      if (isMounted.current) {
        dispatch({
          type: actionTypes.fetchFailed,
          error: e
        });
      }
    }
  };

  return { ...state, fetchOptions, dispatchFetchOptionsStarted, dispatchClearOptions };
};

export default useAddressbookGoogleOptionsService;
